import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FormioComponent, ConfirmationDialogComponent } from 'dp-formio';
import { ToastrService } from 'ngx-toastr';
import { filter, finalize, take, takeUntil, iif } from 'rxjs';
import { showToast } from '../../../toast.helper';
import { BaseComponent } from '../../base.component';
import { DecisionInput } from '../../models/decision-input.model';
import { ExecuteVisitType } from '../../models/execute-visit.model';
import { Summary } from '../../models/summary.model';
import { CriterionsService } from '../../services/criterions.service';
import { AddEditResponseComponent } from './add-edit-response/add-edit-response.dialog';
import { AddEditSummaryComponent } from './add-edit-summary/add-edit-summary.dialog';
import { ItimadCriterionsService } from '../../services/itimad-criterions.service';

@Component({
  selector: 'app-decision-summary',
  templateUrl: './decision-summary.component.html',
  styleUrls: ['./decision-summary.component.scss', '../data-table.scss']
})
export class DecisionSummaryComponent extends BaseComponent implements OnInit {
  @ViewChild(FormioComponent) formioComponent: FormioComponent;
  @Input() title: string;
  @Input() type: string;
  @Input() filter: string;
  decisionInput$ = this.criterionsService.decisionInputs$;
  isLoadingResults: boolean = false;
  expandedFilter: boolean = false;
  finalReport = ExecuteVisitType.FinalReport;
  institutionEntries = ExecuteVisitType.InstitutionEntries;
  finalReportWithInstitutionEntries = ExecuteVisitType.FinalReportWithInstitutionEntries;

  dataSource: Summary[] = [];
  displayedColumns = [
    'summary',
    'finalReportResponse',
    'actions'
  ];

  hasResponse = false;
  enabledSummary = false;

  get isRecommendations() { return this.type == 'recommendations-summaries' };

  constructor(private matDialog: MatDialog,
    private criterionsService: CriterionsService,
    private itimadCriterionsService: ItimadCriterionsService,
    private translateService: TranslateService,
    private toastrService: ToastrService) { super(); }

  ngOnInit() {
    this.getData();
    this.registerCopyEvaluation();

    this.decisionInput$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(input => {

      this.hasResponse = this.isRecommendations &&
        (input.executeVisitType == this.institutionEntries ||
          input.executeVisitType == this.finalReportWithInstitutionEntries);

      this.enabledSummary = !input.viewOnly &&
        (
          input.executeVisitType == this.finalReport ||
          (this.isRecommendations && input.executeVisitType == this.finalReportWithInstitutionEntries)
        );
    });
  }

  showSummary(decisionInput: DecisionInput, summary: Summary = null, isViewOnly: boolean = false) {
    const dir = this.translateService.currentLang == 'ar' ? 'rtl' : 'ltr';
    this.matDialog.open(AddEditSummaryComponent, {
      width: 'min(80%,900px)',
      direction: dir,
      maxHeight: '90vh',
      autoFocus: false,
      restoreFocus: false,
      data: {
        isViewOnly,
        summary: summary?.summaryText || '',
        dir: dir
      }
    })
      .afterClosed()
      .pipe(
        filter(data => !!data),
        take(1)
      )
      .subscribe((result: any) => {
        if (summary) summary.summaryText = result;
        else {
          const model: Summary = {
            visitId: decisionInput.visitMetaData.visitId,
            visitKey: decisionInput.visitMetaData.visitKey,
            reviewerId: decisionInput.visitMetaData.reviewerId,
            summaryText: result,
            deleted: false
          };
          this.dataSource = [...this.dataSource, model];
        }
      });
  }
  undo(index: number) {
    this.dataSource[index].deleted = false;
    this.dataSource = [...this.dataSource];
  }
  deleteSummary(index: number) {
    this.matDialog.open(ConfirmationDialogComponent, {
      direction: this.translateService.currentLang == 'ar' ? 'rtl' : 'ltr',
      data: {
        message: 'forms.common.Are you sure to delete this item',
        buttonText: {
          ok: 'forms.common.yes',
          cancel: 'forms.common.cancel',
        }
      },
      width: "40vw",
      panelClass: 'confirmation-dialog',
    })
      .afterClosed()
      .pipe(filter(data => !!data))
      .subscribe(() => {
        // this.dataSource.splice(index, 1);
        this.dataSource[index].deleted = true;
        this.dataSource = [...this.dataSource];
      });
  }
  showResponse(decisionInput: DecisionInput, summary: Summary) {
    const dir = this.translateService.currentLang == 'ar' ? 'rtl' : 'ltr';
    this.matDialog.open(AddEditResponseComponent, {
      width: 'min(80%,900px)',
      direction: dir,
      maxHeight: '90vh',
      autoFocus: false,
      restoreFocus: false,
      data: {
        summary: summary?.summaryText || '',
        summaryId: summary.id,
        id: summary.finalReportResponseId,
        visitMetaData: decisionInput.visitMetaData,
        viewOnly: decisionInput.viewOnly || decisionInput.executeVisitType != this.institutionEntries,
        dir: dir
      }
    })
      .afterClosed()
      .pipe(
        filter(data => !!data),
        take(1)
      )
      .subscribe((result: any) => {
        summary.finalReportResponseId = result;
      });
  }

  submit(decisionInput: DecisionInput) {
    this.isLoadingResults = true;
    this.dataSource.forEach((item, index) => { item.noteOrder = ++index; });
    this.itimadCriterionsService.saveSummaries(this.type, this.dataSource)
      .pipe(finalize(() => this.isLoadingResults = false))
      .subscribe(data => {
        this.getData();
        // this.dataSource = [...data];
        this.criterionsService.decisionTaken$.next();
        showToast(this.toastrService,
          this.translateService.instant("forms.common.Criteria.Saved done"),
          this.translateService.instant("forms.common.Criteria.Saved Successfully"),
          false
        );
      });
  }

  private getData() {
    this.isLoadingResults = true;
    this.itimadCriterionsService.getSummaries(
      this.criterionsService.decisionInputs$.getValue().visitMetaData.visitKey,
      this.type,
      this.isRecommendations
    ).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isLoadingResults = false)
    ).subscribe(data => {
      this.dataSource = data;
      this.updateResultData();
    });
  }

  private registerCopyEvaluation() {
    this.criterionsService.sendSummary$
      .pipe(
        filter(data => data?.key == this.filter),
        takeUntil(this.destroy$),
      )
      .subscribe(data => {
        const visitMetaData = this.criterionsService.decisionInputs$.getValue().visitMetaData;
        const model: Summary = {
          visitId: visitMetaData.visitId,
          visitKey: visitMetaData.visitKey,
          reviewerId: visitMetaData.reviewerId,
          summaryText: data.value,
          deleted: false
        };
        this.dataSource = [...this.dataSource, model];
      });
  }
  private updateResultData() {
    const value = this.criterionsService.results$.getValue();
    value[this.filter] = this.dataSource;
    this.criterionsService.results$.next(value);
  }
}
